import { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useAuthStore } from '../store/auth';
import { usePageTitle } from '../hooks/usePageTitle';
import { useListFrom, useUrlParams } from '../hooks/useUrlParam';
import { getOfficials } from '../api/client';
import AsyncContent from '../components/shared/AsyncContent';
import EmptyState from '../components/shared/EmptyState';
import FilterBar from '../components/shared/FilterBar';
import Skeleton from '../components/shared/Skeleton';
import AthleteAvatar from '../components/shared/AthleteAvatar';
import { ScaleIcon, TrophyIcon, ClipboardIcon, PeopleIcon } from '../components/shared/Icons';
import { personName, nameMatches, compareNames } from '../utils/personName';
import { useLoad } from '../hooks/useLoad';
import { ALL_OFFICIAL_STATS_ROLES, rate } from '../utils/officialStats';
import { LEVEL_CLS } from '../components/bout/BoutLevelPill';
import type { OfficialRow } from '../types';

const SORT_KEYS = ['name', 'tournaments', 'ring_referee', 'side_judge', 'jury', 'high'] as const;
type SortKey = (typeof SORT_KEYS)[number];

/** #616 — the officials and their counts. For canSeeAllOfficialStats (an
 *  admin, the tournament_manager role, the jury role); a referee goes to the
 *  own page. A counted bout is a scored bout that is not a Walk Over. */
export default function Officials() {
  const { t } = useI18n();
  const { user, hasRole } = useAuthStore();
  const canSeeAllOfficialStats = hasRole(...ALL_OFFICIAL_STATS_ROLES);
  usePageTitle(t.officials.title);
  // #595, #661 — the search and the sort are in the URL, and a card link
  // stores the list URL: Back from an official shows the list as it was.
  const listFrom = useListFrom();
  const [filters, setFilters] = useUrlParams({ q: '', sort: 'name' });
  const search = filters.q;
  const sort: SortKey = (SORT_KEYS as readonly string[]).includes(filters.sort) ? filters.sort as SortKey : 'name';

  // #665, F02 — useLoad holds the loading, the error and Retry. The gate is
  // closed for a user who goes to the own page. A new sign-in reads again.
  const { data: officials, loading, error: loadFailed, reload: load } = useLoad(
    canSeeAllOfficialStats ? () => getOfficials().then(r => r.data.officials) : null,
    [user?.id],
    { context: 'Officials:list', initial: [] as OfficialRow[] },
  );

  const shown = useMemo(() => {
    const rows = officials.filter(o => !search || nameMatches(search, o.first_name, o.last_name));
    // #622, #643 — 'high' sorts by the counted bouts of the Pro and High levels.
    const value = (o: OfficialRow) => (sort === 'high' ? (o.levels?.pro ?? 0) + (o.levels?.high ?? 0) : sort === 'name' ? 0 : o[sort]);
    return [...rows].sort((a, b) => sort === 'name' ? compareNames(a, b) : (value(b) - value(a)) || compareNames(a, b));
  }, [officials, search, sort]);

  if (!user) return null;
  if (!canSeeAllOfficialStats) return <Navigate to={`/officials/${user.id}`} replace />;

  const sortOptions: { value: SortKey; label: string }[] = [
    { value: 'name', label: t.common.name },
    { value: 'tournaments', label: t.officials.tournaments },
    { value: 'ring_referee', label: t.bout.ringReferee },
    { value: 'side_judge', label: t.bout.sideJudge },
    { value: 'jury', label: t.bout.jury },
    { value: 'high', label: t.officials.sortHigh },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-black">{t.officials.title}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{t.officials.countHint}</p>
      </div>

      {/* The sort select has no empty option, so it goes into `actions`. */}
      <FilterBar
        search={search}
        onSearchChange={q => setFilters({ q })}
        searchPlaceholder={t.officials.searchPlaceholder}
        searchTestId="officials-search"
        actions={(
          <label className="text-sm text-gray-500 inline-flex items-center gap-2">
            {t.officials.sortBy}
            <select value={sort} onChange={e => setFilters({ sort: e.target.value })} data-testid="officials-sort" className="input w-auto">
              {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        )}
      />

      <AsyncContent
        loading={loading}
        error={!!loadFailed}
        onRetry={load}
        isEmpty={shown.length === 0}
        skeleton={<Skeleton.List shape="athlete-row" count={6} cols={3} />}
        empty={<EmptyState icon={<ScaleIcon className="w-12 h-12" />}
          title={search && officials.length > 0 ? t.officials.noMatch : t.officials.noOfficials} />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shown.map(o => (
            <Link key={o.id} to={`/officials/${o.id}`} state={listFrom} data-testid="official-card" data-official-id={o.id}
              className="card p-3 flex items-start gap-3 hover:shadow-md transition-shadow">
              <AthleteAvatar firstName={o.first_name || '?'} lastName={o.last_name || '?'} photoUrl={o.photo_url} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold break-words">{personName(o.first_name, o.last_name)}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {/* A label in each pill: a title shows no text on a touch screen. */}
                  <span className="badge badge-gray gap-1 whitespace-nowrap"><TrophyIcon className="w-3.5 h-3.5" />{t.officials.tournaments} {o.tournaments}</span>
                  <span className="badge badge-gray gap-1 whitespace-nowrap"><ScaleIcon className="w-3.5 h-3.5" />{t.bout.ringReferee} {o.ring_referee}</span>
                  <span className="badge badge-gray gap-1 whitespace-nowrap"><PeopleIcon className="w-3.5 h-3.5" />{t.bout.sideJudge} {o.side_judge}</span>
                  <span className="badge badge-gray gap-1 whitespace-nowrap"><ClipboardIcon className="w-3.5 h-3.5" />{t.bout.jury} {o.jury}</span>
                  {/* #622, #643 — the counted bouts of the Pro and the High level. */}
                  {!!o.levels?.pro && <span data-testid="official-card-pro" className={`badge whitespace-nowrap ${LEVEL_CLS.pro}`}>{t.officials.proPill.replace('{n}', String(o.levels.pro))}</span>}
                  {!!o.levels?.high && <span data-testid="official-card-high" className={`badge whitespace-nowrap ${LEVEL_CLS.high}`}>{t.officials.highPill.replace('{n}', String(o.levels.high))}</span>}
                </div>
                {/* #616, phase 2 — the quality pills, where the official has the data. */}
                {o.quality && (o.quality.agreement.compared > 0 || o.quality.dropped.rounds > 0 || o.quality.stoppages.stoppages > 0 || o.quality.jury.overrides_differ > 0) && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="official-card-quality">
                    {o.quality.agreement.compared > 0 && <span className="badge bg-indigo-50 text-indigo-700 whitespace-nowrap">{t.officials.agreementShort} {rate(o.quality.agreement.matched, o.quality.agreement.compared)}%</span>}
                    {o.quality.dropped.rounds > 0 && <span className="badge bg-indigo-50 text-indigo-700 whitespace-nowrap">{t.officials.dropped} {rate(o.quality.dropped.dropped, o.quality.dropped.rounds)}%</span>}
                    {o.quality.stoppages.stoppages > 0 && <span className="badge bg-indigo-50 text-indigo-700 whitespace-nowrap">{t.officials.stoppagesShort} {o.quality.stoppages.stoppages}</span>}
                    {o.quality.jury.overrides_differ > 0 && <span className="badge bg-indigo-50 text-indigo-700 whitespace-nowrap">{t.officials.juryTotals} {o.quality.jury.overrides_differ}</span>}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </AsyncContent>
    </div>
  );
}
